<template>
  <select-dict
    v-model="currentGlassSort"
    :selectList="glassSortsList"
    :select-first-item="true"
  />
</template>

<script setup lang="ts">
import { computed, defineModel, watch, ref } from "vue";

import SelectDict from "@/components/UI/SelectDict.vue";
import { useDictStore } from "@/stores/dictStore";
import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { usePartitionStore } from "@/stores/new/partition.store";
import { Dictionary } from "@/models/dictionary";

const partitionsStore = usePartitionStore();
const dictStore = useDictStore();
const estimateStore = useNewEstimateStore();

const installationSystem = computed(
  () => estimateStore.estimate.installedSystemCode,
);
const activeThickStructureType = computed(
  () => partitionsStore.activeGlassThinknessPartition?.structureType,
);

const availableGlassSorts = dictStore.findGlassSort(installationSystem.value);
const glassSortsList = ref<Dictionary[]>([]);
const currentGlassSort = defineModel<string>();

// set default glass sorts list
watch(
  () => availableGlassSorts,
  () => {
    glassSortsList.value = availableGlassSorts;
  },
  {
    immediate: true,
  },
);

const filterGlassSorts = (el) => {
  const glassType = dictStore.findGlassSortThicknesses(el.code);
  const glassThickness = glassType?.thicknesses?.find(
    (thick) => thick === activeThickStructureType.value,
  );

  return glassThickness;
};

// update glass sorts list if thick changes
watch(
  () => [currentGlassSort.value, activeThickStructureType.value],
  () => {
    const filteredGlassSorts = availableGlassSorts.filter((el) => {
      const glassThickness = filterGlassSorts(el);
      if (glassThickness) {
        return el;
      }
    });

    glassSortsList.value = filteredGlassSorts;
  },
  {
    immediate: true,
  },
);

// update default glass sort if thick changes
watch(
  () => activeThickStructureType.value,
  (currentGlassThickness, oldGlassThickness) => {
    if (typeof oldGlassThickness !== "string") {
      return;
    }

    const defaultGlassType = glassSortsList.value.find((el) => {
      const glassType = dictStore.findGlassSortThicknesses(el.code);
      const glassThickness = glassType?.thicknesses?.find(
        (thick) => thick === currentGlassThickness,
      );

      if (glassThickness) {
        return el;
      }
    });
    currentGlassSort.value = defaultGlassType?.code;
  },
  {
    immediate: true,
  },
);
</script>

<style lang="scss" scoped></style>
